define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"csi",
	"csi/config",
	"csi/enums",
	"csi/form/Widget",
	"csi/reflection",
	"csi/store/Names",
	"csi/xml",
	"csi/xml/Search"
], function (array, declare, csi, config, enums, FormWidget, reflection, Names, xml, Search) {

	var MODE = "Mode",
		TRACKING_LEVEL = "TrackingLevel",
		TRACKING_TYPE = "TrackingType",
		TRACKED_SUBTYPE = "TrackedSubtype",
		TRACKED_SUBTYPE2 = "TrackedSubtype2",
		TRACKED_PHASE = "TrackedPhase",
		TRACKED_STATE = "TrackedState",
		CALENDAR_LEVEL_PREFIX ="centric://REFLECTION/INSTANCE/CalendarLevel/",
		VALIDATION_RULE_GROUP = "ValidationRuleGroup";

	// Disable mode if any applicable type is not a DataSheetContainer
    function isModeDisabled(type, applicableForTypes) {
		const isTypeDataSheetContainer = (type) => reflection.getClass(type).isDerivedFrom("DataSheetContainer");
		if (!isTypeDataSheetContainer(type) && type !== "WbsAdmin") {
			return true;
		}
		if (type === "WbsAdmin") {
			for (const item of applicableForTypes) {
				const applicableType = xml.enumGetText(item);
				if (!isTypeDataSheetContainer(applicableType)) {
					return true;
				}
			}
		}
		return false;
	}
	// based on Calendar.ProtoActivityForm
	return declare(FormWidget, {
		bWriteVisible: false,

		// Object: map of attributes to expressions indicating if should be hidden
		_hideExpressions: {
			MustMatchAll: "attr('Mode') != 'WbsElement.Mode:Tracking'",
			TrackingLevel: "attr('Mode') != 'WbsElement.Mode:Tracking'",
			TrackingType: "attr('Mode') != 'WbsElement.Mode:Tracking'",
			ValidationRuleGroup: "attr('Mode') == 'WbsElement.Mode:Tracking'"
		},

		getFieldStore: function (/*csi.def.Field*/field, /*String*/value) {
			// summary: special handling for some stores
			var trackingType = this._getTrackingType();
			var store;

			if (field.m_id == TRACKED_SUBTYPE) {
				// build subtype field based on current tracked type
				store = enums.getStore(trackingType && trackingType.SubType ? trackingType.SubType : "NoTrackedSubtype");
			} else if (field.m_id == TRACKED_SUBTYPE2) {
				// build subtype field based on current tracked type
				store = this._getTrackableSubtypes(trackingType);
			} else if (field.m_id == TRACKED_STATE) {
				// special handling for tracked types with own states
				store = this._createStateStore(field, trackingType);
			} else if (field.m_id == "ValidationRuleGroup") {
				var parentNode = this.cacheView.getNode(this.defView.m_formData.getParent().__Parent__);
				if (parentNode != null) {
					var search = new Search();
					var query = search.getQuery();
					query.Node("Type", "EQ", "ConfigurableValidationRuleGroup").Node("Name", "NE", "Default").Attr("AppliedOn", "NE", "0");
					if (parentNode.$Type == "Style")
						query.RefAttr("__Parent__", "EQ", "centric://REFLECTION/BusinessObject/Style");
					else if (parentNode.$Type == "Material")
						query.RefAttr("__Parent__", "EQ", "centric://REFLECTION/BusinessObject/Library Item, Material");
					else if (parentNode.$Type == "Collection")
						query.RefAttr("__Parent__", "EQ", "centric://REFLECTION/BusinessObject/Hierarchy, Collection");
					else if (parentNode.$Type == "Colorway")
						query.RefAttr("__Parent__", "EQ", "centric://REFLECTION/BusinessObject/Colorway");
					else if (parentNode.$Type == "SKU")
						query.RefAttr("__Parent__", "EQ", "centric://REFLECTION/BusinessObject/Style SKU");

					store = new Names({
						typeNode: "ConfigurableValidationRuleGroup",
						request: search.getRequest()
					});
				}
			}

			return store || this.inherited(arguments);
		},

		/**
		 * @override
		 * Attach form node to parent WBS and create tracking pattern with appropriate values if mode set to tracking
		 */
		onWriteFormReport(field, writer, formInfo) {
			const { nodeUrl, parentUrl } = formInfo;
			const trackingPattern = "TrackingPattern";
			const trackingPath = xml.path(trackingPattern);
			const isTracking = this.getFieldValue("Mode") === "WbsElement.Mode:Tracking";
			const parent = this.cacheView.get(parentUrl);
			const wbsUrl = parent.WBS || parent.$URL;

			if (isTracking) {
				writer.createNode(null, trackingPattern).attach(nodeUrl, trackingPattern);
			}

			for (const f of this.defView.m_fields) {
				if (f.m_path === trackingPath) {
					f.omitFromReport = !isTracking;
				}
			}

			writer.changeNode(nodeUrl).changeAttribute("WBS", xml.ATTR_REF, wbsUrl);
		},

		_buildView: function () {
			// summary: make sure WBS is in cache to resolve queries
			this.cacheView.cacheNode(this.defView.m_formData.getParent());
			// Disable the 'Mode' field if the wbs parent is not a DataSheetContainer
			const wbs = this.cacheView.cacheNode(this.defView.m_formData.getParent());
			const parent = this.cacheView.get(wbs.__Parent__);
			const type = parent.$Type;

			// Check if parent is not a DataSheetContainer or WbsAdmin
			const disableMode = isModeDisabled(type, wbs.ApplicableForTypes);
			this.setFieldDisabled("Mode", disableMode);
			this.inherited(arguments);
		},

		_createStateStore: function (field, trackingType) {
			return enums.getStore(field.getEnumId(), null, function (v) {
				var stateName = xml.enumGetText(v);
				if (!stateName)
					return true;

				if (!trackingType)
					return false;

				return array.some(trackingType.TrackingStates, function (state) {
					return xml.enumGetText(state) == stateName;
				});
			});
		},

		_getFieldSelectRootURL: function (field) {
			// summary: all queries but levels and phases are relative to WBS
			return (field.m_id == TRACKING_TYPE || field.m_id == TRACKED_PHASE)
				? this.inherited(arguments)
				: this.defView.m_formData.getParent().$URL;
		},

		_getTrackableSubtypes: function (trackingType) {
			// summary: return subtypes of currently selected calendar tracking type
			return trackingType ? config.getSubtypeStore(trackingType.$Name) : null;
		},

		_getTrackingType: function () {
			// summary: return currently selected calendar tracking type
			return this.cacheView.getNode(this.defView.getFieldValue(TRACKING_TYPE), true);
		},

		_updateFieldVisibility: function (/*csi.def.Field?*/field) {
			// summary: reset hidden field values
			var b1 = this.inherited(arguments);
			var b2;

			// update fields based on tracking type - do so only when first building view or when tracking type changes
			if (!field || (field.m_id == TRACKING_TYPE)) {
				var isTracking = this.defView.getFieldValue(MODE) == "WbsElement.Mode:Tracking";
				var trackingType = this._getTrackingType();
				var trackableSubtypes = this._getTrackableSubtypes(trackingType);

				this.setFieldHidden(TRACKED_SUBTYPE, !isTracking || !trackingType || !trackingType.SubType);
				this.setFieldHidden(TRACKED_SUBTYPE2, !isTracking || !trackableSubtypes);
				this.setFieldHidden(TRACKED_PHASE, !isTracking || !trackingType || !trackingType.TrackingPhases.length);
				this.setFieldHidden(TRACKED_STATE, !isTracking || !trackingType || !trackingType.TrackingStates.length);

				b2 = true;
			}

			if (field?.m_id === "Mode") {
				const isTrackingMode = this.defView.getFieldValue(MODE) == "WbsElement.Mode:Tracking";
				const wbs = this.cacheView.cacheNode(this.defView.m_formData.getParent());
				if (isTrackingMode && wbs.ApplicableForTypes.length === 1) {
					const trackingLevelField = this.getField(TRACKING_LEVEL);
					const applicableType = xml.enumGetText(wbs.ApplicableForTypes[0]);
					this.setFieldValue(trackingLevelField, CALENDAR_LEVEL_PREFIX + applicableType);
					this.setFieldDisabled(trackingLevelField, true);
				}
			}

			if (b1) {
				// ensures fields disappear when switching back from Tracking to Manual
				array.forEach(this.defView.m_fields, function (f) {
					if (!this.isFieldVisible(f)) {
						this.setFieldValue(f, f.m_rca.getValue());
					}
				}, this);
			}

			return b1 || b2;
		}
	});
});